import { useMemo, useState } from 'react';
import { App, Button, Card } from 'antd';
import { DataTable } from '../../components/DataTable';
import { HintBanner, HintToggle, useDismissibleHint } from '../../components/DismissibleHint';
import { StatusTag } from '../../components/StatusTag';
import type { ColumnsType } from 'antd/es/table';
import { ArrowCounterClockwise, PencilSimple } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageBody, PageHeader } from '../../app/AppLayout';
import { ErrorState, LoadingState } from '../../components/PageStates';
import { ApiError, NetworkError } from '../../api/client';
import { GROUP_LABELS, configApi, configKeys, groupOf, type SystemConfig } from './api';
import { ConfigEditModal } from './ConfigEditModal';
import { ConfigResetModal } from './ConfigResetModal';
import { tokens } from '../../theme';

/** Số nguyên kèm đơn vị, bật/tắt thành chữ — không bắt người vận hành đọc 0 và 1. */
function formatValue(config: SystemConfig, value: number): string {
  if (config.type === 'Boolean') return value !== 0 ? 'Bật' : 'Tắt';
  return config.unit ? `${value} ${config.unit}` : String(value);
}

export function ConfigPage() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  const [editing, setEditing] = useState<SystemConfig | null>(null);
  const [resetting, setResetting] = useState<SystemConfig | null>(null);
  const limitsHint = useDismissibleHint('config-limits');

  const configsQuery = useQuery({ queryKey: configKeys.all, queryFn: configApi.list });

  const reportError = (error: unknown, fallback: string) => {
    message.error(
      error instanceof ApiError || error instanceof NetworkError ? error.message : fallback,
    );
  };

  const setMutation = useMutation({
    mutationFn: ({ key, value, reason }: { key: string; value: number; reason: string }) =>
      configApi.set(key, value, reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: configKeys.all });
      setEditing(null);
      message.success('Đã lưu. Thay đổi có tác dụng ngay, không cần khởi động lại.');
    },
    onError: (error) => reportError(error, 'Không lưu được thay đổi.'),
  });

  const resetMutation = useMutation({
    mutationFn: ({ key, reason }: { key: string; reason: string }) => configApi.reset(key, reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: configKeys.all });
      setResetting(null);
      message.success('Đã trả về mặc định.');
    },
    onError: (error) => reportError(error, 'Không trả về mặc định được.'),
  });

  const configs = useMemo(() => configsQuery.data ?? [], [configsQuery.data]);
  const overriddenCount = configs.filter((config) => config.isOverridden).length;

  // Gom theo tiền tố của khóa để bảng không thành danh sách phẳng mười mấy tên kỹ thuật
  const grouped = useMemo(() => {
    const map = new Map<string, SystemConfig[]>();
    for (const config of configs) {
      const group = groupOf(config.key);
      map.set(group, [...(map.get(group) ?? []), config]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b, 'vi'));
  }, [configs]);

  const columns: ColumnsType<SystemConfig> = [
    {
      title: 'Thông số',
      key: 'key',
      width: 340,
      render: (_, config) => (
        <>
          <div style={{ fontWeight: 600 }}>{config.key.split(':').slice(1).join(' · ')}</div>
          <div style={{ fontSize: 12, color: tokens.color.ink2 }}>
            {config.description}
          </div>
        </>
      ),
    },
    {
      title: 'Đang dùng',
      key: 'value',
      width: 200,
      render: (_, config) => (
        <>
          <span style={{ fontWeight: 600 }}>{formatValue(config, config.value)}</span>
          {/* Có chữ đi kèm chứ không chỉ dựa vào màu của thẻ */}
          {config.isOverridden ? (
            <StatusTag color="warning" style={{ marginLeft: 8 }}>
              Đã đổi
            </StatusTag>
          ) : (
            <StatusTag color="default" style={{ marginLeft: 8 }}>
              Mặc định
            </StatusTag>
          )}
          {config.isOverridden && (
            <div style={{ fontSize: 12, color: tokens.color.ink3 }}>
              Mặc định {formatValue(config, config.defaultValue)}
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Lý do thay đổi',
      key: 'reason',
      width: 260,
      render: (_, config) =>
        config.reason ? (
          <span style={{ fontSize: 12.5 }}>{config.reason}</span>
        ) : (
          <span style={{ color: tokens.color.ink3 }}>—</span>
        ),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 200,
      render: (_, config) => (
        // `aria-label` kèm tên khóa: mười mấy dòng thì có mười mấy nút cùng đọc là
        // "Sửa", người dùng trình đọc màn hình không biết mình đang ở thông số nào
        <>
          <Button
            size="small"
            icon={<PencilSimple size={14} />}
            aria-label={`Sửa ${config.key}`}
            onClick={() => setEditing(config)}
          >
            Sửa
          </Button>
          <Button
            size="small"
            type="text"
            disabled={!config.isOverridden}
            icon={<ArrowCounterClockwise size={14} />}
            style={{ marginLeft: 4 }}
            aria-label={`Trả ${config.key} về mặc định`}
            onClick={() => setResetting(config)}
          >
            Mặc định
          </Button>
        </>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Thông số vận hành"
        subtitle="Đổi từ đây có tác dụng ngay, không cần deploy lại"
        hint={<HintToggle hint={limitsHint} />}
      />
      <PageBody>
        {configsQuery.isPending && <LoadingState />}

        {configsQuery.isError && (
          <ErrorState error={configsQuery.error} onRetry={() => void configsQuery.refetch()} />
        )}

        {configsQuery.isSuccess && (
          <>
            <HintBanner
              hint={limitsHint}
              type={overriddenCount > 0 ? 'warning' : 'info'}
              message={
                overriddenCount > 0
                  ? `${overriddenCount} thông số đang lệch khỏi mặc định`
                  : 'Mọi thông số đang chạy bằng mặc định'
              }
              // Nói rõ giới hạn của màn này. Không nói thì người vận hành đi tìm mãi
              // một công tắc cố ý không có ở đây.
              description="Chỉ đổi được giá trị của những thông số đã khai sẵn. Khóa ký, chuỗi kết nối và số vòng băm mật khẩu cố ý không nằm ở đây."
            />

            {grouped.map(([group, items]) => (
              <Card
                key={group}
                title={GROUP_LABELS[group] ?? group}
                size="small"
                style={{ marginBottom: tokens.space.sectionGap }}
              >
                <DataTable toolbar={false}
                  rowKey="key"
                  size="small"
                  columns={columns}
                  dataSource={items}
                  pagination={false}
                  // Cửa sổ hẹp thì cuộn ngang, không bóp cột. Không đặt thì tiêu đề
                  // "Lý do thay đổi" xuống dòng mỗi chữ một hàng và bảng thành không đọc nổi.
                  scroll={{ x: 1000 }}
                />
              </Card>
            ))}
          </>
        )}
      </PageBody>

      <ConfigEditModal
        config={editing}
        submitting={setMutation.isPending}
        onCancel={() => setEditing(null)}
        onSubmit={(value, reason) => {
          if (editing) setMutation.mutate({ key: editing.key, value, reason });
        }}
      />

      <ConfigResetModal
        config={resetting}
        submitting={resetMutation.isPending}
        formatValue={formatValue}
        onCancel={() => setResetting(null)}
        onSubmit={(reason) => {
          if (resetting) resetMutation.mutate({ key: resetting.key, reason });
        }}
      />
    </>
  );
}
